import React from "react";
import { useTranslation } from "react-i18next";

/* 제품 소개 (대표 제품 + 제품 상세 통합)
   - 제품마다 이미지와 설명을 좌우로 배치하고, 상세(환자용 / 의료진용)를 그 아래에 붙입니다.
   - 이전에는 카드 목록과 상세가 따로 있어 같은 제품이 두 번 나왔습니다. */

const renderMultiline = (text) =>
  String(text)
    .split("\n")
    .map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1.1 2.8-2.2c.9-1.2 1.2-2.4 1.2-2.5 0 0-2.4-.9-2.4-3.6ZM14.2 5.9c.6-.8 1-1.9.9-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-.9 2.9 1 .1 2-.5 2.7-1.3Z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M4 3.2v17.6c0 .6.6 1 1.2.7l13.4-8.8c.5-.3.5-1.1 0-1.4L5.2 2.5c-.6-.3-1.2.1-1.2.7Z" />
  </svg>
);

function DetailColumns({ ns, productKey, numbered }) {
  const { t } = useTranslation();
  const base = `${ns}.details.${productKey}`;
  const opts = { returnObjects: true };
  const left = t(`${base}.left`, opts);
  const right = t(`${base}.right`, opts);
  const evidence = t(`${base}.evidence`, opts);
  if (!left || typeof left !== "object") return null;

  const column = (col, withNumbers) => (
    <div className="ps-detail-col">
      <p className="ps-detail-label">{col.label}</p>
      <p className="ps-detail-title">{col.title}</p>
      <ol className="ps-detail-list">
        {(col.items || []).map((item, i) => (
          <li key={i}>
            <span className={withNumbers ? "ps-no" : "ps-dot"}>
              {withNumbers ? i + 1 : ""}
            </span>
            <div>
              <p className="ps-item-title">{item.title}</p>
              <p className="ps-item-desc">{item.desc}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <div className="ps-detail">
      <div className="ps-detail-grid">
        {column(left, numbered)}
        {column(right, false)}
      </div>
      {evidence && evidence.text && (
        <div className="ps-evidence">
          <span className="ps-evidence-badge">EVIDENCE</span>
          <div>
            <p className="ps-evidence-text">{evidence.text}</p>
            <p className="ps-source">{evidence.source}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductShowcase({ ns, items }) {
  const { t } = useTranslation();

  return (
    <div className="ps-wrap">
      {items.map((item, idx) => {
        const base = `${ns}.services.items.${item.key}`;
        const btnText = item.site
          ? t(`${base}.btnText`, { defaultValue: "" })
          : "";
        return (
          <article
            className={`ps-product ${idx % 2 === 1 ? "ps-product-reverse" : ""}`}
            key={item.key}
          >
            <div className="ps-head">
              <div className="ps-media">
                <img src={item.img} alt={t(`${base}.title`)} loading="lazy" />
              </div>
              <div className="ps-summary">
                <p className="ps-index">{`0${idx + 1}`}</p>
                <h3 className="ps-name">{t(`${base}.title`)}</h3>
                <p className="ps-desc">
                  {renderMultiline(t(`${base}.content`))}
                </p>
                <div className="ps-actions">
                  {item.site && btnText && (
                    <a
                      className="ps-site"
                      href={item.site}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {btnText} &rsaquo;
                    </a>
                  )}
                  {(item.appStore || item.play) && (
                    <div className="ps-stores">
                      {item.appStore && (
                        <a href={item.appStore} target="_blank" rel="noopener noreferrer">
                          <AppleIcon />
                          {t("common.store.appStore")}
                        </a>
                      )}
                      {item.play && (
                        <a href={item.play} target="_blank" rel="noopener noreferrer">
                          <PlayIcon />
                          {t("common.store.googlePlay")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {item.detailKey && (
              <DetailColumns
                ns={ns}
                productKey={item.detailKey}
                numbered={item.numbered}
              />
            )}
          </article>
        );
      })}
    </div>
  );
}
